import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase-client";

export default function PlatformFinanceReset({
  row,
  busy,
  run,
  parseAmount,
  format,
}) {
  const [reason, setReason] = useState("");
  const [reserve, setReserve] = useState(
    String((row.reserve ?? 0) / row.minorUnitFactor),
  );
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const amountMinor = parseAmount(reserve, row.minorUnitFactor);
  const change = (setter) => (event) => {
    setter(event.target.value);
    setPreview(null);
    setConfirmed(false);
  };
  const prepare = (event) => {
    event.preventDefault();
    setPreview(null);
    setConfirmed(false);
    run(async () => {
      const { data } = await httpsCallable(
        functions,
        "previewPlatformFinanceReset",
        { timeout: 300000 },
      )({
        currency: row.currency,
        amountMinor,
        reason: reason.trim(),
      });
      setPreview(data);
    });
  };
  const commit = () =>
    run(async () => {
      await httpsCallable(functions, "resetPlatformFinance", {
        timeout: 300000,
      })({ previewId: preview.previewId, confirmSettled: true });
      setPreview(null);
      setConfirmed(false);
      setReason("");
      return "Historical payments settled. Old queued transfers cancelled and withdrawal calculation restarted.";
    }, true);
  return (
    <details
      className="platform-finance__details"
      aria-label="Settle historical payments"
    >
      <summary>Settle historical payments and start again</summary>
      <div className="platform-finance__details-body">
        <p>
          Use this after reconciling all clubs in {row.currency}, including
          overpayments and manual adjustments. This clears the current
          historical obligations and checks from your withdrawal calculation,
          and cancels the old queued transfers shown in the preview. Payment
          records remain available for accounting.
        </p>
        <p className="platform-finance__muted">
          New payments count normally. Later refunds, reversals or changes to
          settled records require a new review. Future event payouts must be
          resolved separately before resetting.
        </p>
        <form className="platform-finance__reset-form" onSubmit={prepare}>
          <label>
            Reconciliation note
            <textarea
              value={reason}
              onChange={change(setReason)}
              minLength={10}
              maxLength={1000}
              required
              disabled={busy}
              placeholder="Explain the overpayments or adjustments that settle the old balance."
            />
          </label>
          <label>
            Reserve to keep after reset ({row.currency})
            <input
              type="text"
              inputMode="decimal"
              value={reserve}
              onChange={change(setReserve)}
              disabled={busy}
            />
          </label>
          <p className="platform-finance__muted">
            Enter 0 to keep no extra reserve. The starting balance comes
            directly from Stripe.
          </p>
          <button
            type="submit"
            disabled={busy || amountMinor === null || reason.trim().length < 10}
          >
            Preview historical reset
          </button>
        </form>
        {preview && (
          <section
            className="platform-finance__reset-preview"
            aria-label="Historical reset preview"
            aria-live="polite"
          >
            <h4>Review before resetting</h4>
            <dl className="platform-finance__breakdown">
              <div>
                <dt>Available in Stripe now</dt>
                <dd>{format(preview.available)}</dd>
              </div>
              <div>
                <dt>Still settling</dt>
                <dd>{format(preview.pending)}</dd>
              </div>
              <div>
                <dt>Historical club obligations to settle</dt>
                <dd>{format(preview.owedToClear)}</dd>
              </div>
              <div>
                <dt>Historical unmatched funds to settle</dt>
                <dd>{format(preview.heldToClear)}</dd>
              </div>
              <div>
                <dt>Queued transfers to cancel</dt>
                <dd>{preview.cancelledTransfers}</dd>
              </div>
              <div>
                <dt>Reserve after reset</dt>
                <dd>{format(preview.reserve)}</dd>
              </div>
              <div>
                <dt>Withdrawal allowance after reset</dt>
                <dd>{format(preview.safeToWithdraw)}</dd>
              </div>
            </dl>
            {preview.clubs?.length > 0 && (
              <ul className="platform-finance__clubs">
                {preview.clubs.map((club, index) => (
                  <li key={index}>
                    <span>{club.name}</span>
                    <strong>{format(club.owed)} to settle</strong>
                  </li>
                ))}
              </ul>
            )}
            <p>
              {preview.counts.charges} Stripe payments and{" "}
              {preview.counts.transfers} transfers will be marked as historical.
            </p>
            <p>{preview.reason}</p>
            <label className="platform-finance__reset-confirm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                disabled={busy}
              />
              <span>
                I have reconciled all historical club balances shown above and
                confirm the queued transfers should be cancelled.
              </span>
            </label>
            <button
              type="button"
              disabled={busy || !confirmed}
              onClick={commit}
            >
              Confirm reset and cancel old transfers
            </button>
          </section>
        )}
      </div>
    </details>
  );
}
